import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { getDisplayValueFromList, priorityOptionsList } from 'components/utils';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import TextArea from 'components/form-components/TextArea';
import TextField from 'components/form-components/TextField';
import ScheduleSection from './ScheduleSection';
import UserAndGroupSelection from 'components/common-components/UserAndGroupSelection';
import { getUserGroupDisplayValue } from 'pages/tasks/utils';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';

const BasicTab = ({
  values,
  setValues,
  formsOptions,
  formCompanyFieldOptionsMap,
  formCompanyFieldValueOptionsMap,
  displayValueLookup,
  isEdit,
  allowChangeAssignee,
}) => {
  const { t } = useTranslation();
  const [allUsersAndGroups, setAllUsersAndGroups] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const displayText = useMemo(() => {
    return getUserGroupDisplayValue(
      allUsersAndGroups?.users.filter((u) => values.assignedUsers.includes(u.id)),
      allUsersAndGroups?.groups.filter((g) => values.assignedGroups.includes(g.id)),
    );
  }, [values.assignedUsers, values.assignedGroups, allUsersAndGroups]);

  const handleChange = (field, value) => {
    setValues((prevValues) => {
      const updatedValues = { ...prevValues };
      // reset companyField dependent values whenever form changes
      if (field === 'form' && prevValues.form !== value) {
        updatedValues.companyField = null;
        updatedValues.companyFieldRowId = null;
        updatedValues.companyFieldValue = null;
        updatedValues.autoFillCompanyField = false;
      }

      // reset companyField dependent values whenever companyfield changes
      if (field === 'companyField' && prevValues.companyField !== value) {
        updatedValues.companyFieldRowId = null;
        updatedValues.companyFieldValue = null;
      }

      if (field === 'companyFieldRowId' && prevValues.companyFieldRowId !== value) {
        updatedValues.companyFieldValue =
          displayValueLookup[updatedValues.companyField + '_' + value] || null;
      }

      // reset dependent recurring fields
      if (field === 'recurringFrequency' && prevValues.recurringFrequency !== value) {
        updatedValues.recurringInterval = null;
        updatedValues.daysOfWeek = [];
        updatedValues.repeatsOn = [];
      }

      updatedValues[field] = value;

      return updatedValues;
    });

    setTouched((prevTouched) => ({ ...prevTouched, [field]: true }));
    setErrors((prevErrors) => ({ ...prevErrors, [field]: undefined }));
  };

  const handleAssignmentDoneAction = (data) => {
    setValues((prev) => ({ ...prev, assignedUsers: data.users, assignedGroups: data.groups }));
  };

  return (
    <div className='d-flex flex-column gap-3'>
      {isEdit && (
        <span className='px-2 py-1 theme-background-white-3 rounded-md align-self-start'>
          {`ST-${values.referenceNumber}`}
        </span>
      )}
      <div className='d-flex flex-column gap-1'>
        <span>{t('field_title')}</span>
        <TextField
          name='title'
          classes='w-auto'
          value={values.title}
          error={errors.title}
          touched={touched.title}
          handleChange={handleChange}
          placeholder='field_placeholder_add_title'
          shrinkFieldOnly
        />
      </div>
      <div className='d-flex flex-column gap-1'>
        <span>{t('field_description')}</span>
        <TextArea
          name='description'
          classes='w-auto'
          value={values.description}
          error={errors.description}
          touched={touched.description}
          handleChange={handleChange}
          placeholder='field_placeholder_write_task_description'
          rows={2}
        />
      </div>
      <div className='w-100 d-flex flex-column gap-1'>
        <span>{t('field_form')}</span>
        <MultiLevelSelect
          name='form'
          options={{ children: formsOptions }}
          value={[]}
          handleChange={(field, value) => handleChange(field, value[0])}
          toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                {displayValueLookup[values.form] ?? t('select_select')}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          iconVariant={'original'}
          fullWidth
        />
      </div>
      <div className='d-flex gap-3'>
        <div className='w-100 d-flex flex-column gap-1'>
          <span>{t('field_company_field')}</span>
          <MultiLevelSelect
            name='companyField'
            options={{ children: formCompanyFieldOptionsMap[values.form] ?? [] }}
            value={[]}
            handleChange={(field, value) => handleChange(field, value[0])}
            toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                  {displayValueLookup[values.companyField] ?? t('select_select')}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
            iconVariant={'original'}
            fullWidth
          />
        </div>

        <div className='w-100 d-flex flex-column gap-1'>
          <span>{t('form_value')}</span>
          <MultiLevelSelect
            name='companyFieldRowId'
            options={{ children: formCompanyFieldValueOptionsMap[values.companyField] ?? [] }}
            value={[]}
            handleChange={(field, value) => handleChange(field, value[0])}
            toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
            toggleComponent={
              <>
                <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                  {displayValueLookup[values.companyField + '_' + values.companyFieldRowId] ??
                    t('select_select')}
                </label>
                <img src={chevronGreyIcon} className='rotate-180' />
              </>
            }
            iconVariant={'original'}
            fullWidth
            disabled={!values.companyField}
          />
        </div>
      </div>
      {values.companyField && (
        <SingleCheckBoxField
          name={'autoFillCompanyField'}
          value={values.autoFillCompanyField}
          text={<span>{t('text_auto_fill_company_field')}</span>}
          handleChange={handleChange}
        />
      )}
      <div className='d-flex flex-column gap-1'>
        <span>{t('field_priority')}</span>
        <MultiLevelSelect
          name='priority'
          options={{ children: priorityOptionsList }}
          value={[]}
          handleChange={(field, value) => handleChange(field, value[0])}
          toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
          toggleComponent={
            <>
              <label className='theme-size-0_9 cursor-pointer overflow-hidden'>
                {t(getDisplayValueFromList(priorityOptionsList, values.priority, 'select_select'))}
              </label>
              <img src={chevronGreyIcon} className='rotate-180' />
            </>
          }
          iconVariant={'original'}
          fullWidth
        />
      </div>
      {allowChangeAssignee && (
        <div>
          <label className='mb-2'>{t('field_assigned_to')}</label>
          <UserAndGroupSelection
            doneAction={handleAssignmentDoneAction}
            onDataLoad={setAllUsersAndGroups}
            toggleComponent={
              <div className='d-flex justify-content-between align-items-center border-grey-1-h-1 cursor-pointer rounded-sm p-2'>
                <div>
                  <img src={userGreyIcon} />
                  <span className='theme-size-0_9 mx-2'>
                    {displayText ? displayText : t('text_select_user_or_group')}
                  </span>
                </div>
                <img src={chevronGreyIcon} className='rotate-180' />
              </div>
            }
            existingUserIds={values.assignedUsers}
            existingGroupIds={values.assignedGroups}
          />
        </div>
      )}

      <ScheduleSection values={values} handleChange={handleChange} isEdit={isEdit} />
    </div>
  );
};

BasicTab.propTypes = {
  values: PropTypes.object.isRequired,
  setValues: PropTypes.func.isRequired,
  formsOptions: PropTypes.array.isRequired,
  formCompanyFieldOptionsMap: PropTypes.object.isRequired,
  formCompanyFieldValueOptionsMap: PropTypes.object.isRequired,
  displayValueLookup: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  allowChangeAssignee: PropTypes.bool.isRequired,
};

export default BasicTab;
